import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import ProductCard from "../components/ProductCard";
import { ArrowUpRight, Sparkles, Truck, ShieldCheck, RotateCcw } from "lucide-react";
import { fmt } from "../lib/money";
import YouTubeHero from "../components/YouTubeHero";
import AffiliateMonthWidget from "../components/AffiliateMonthWidget";
import { useAuth } from "../context/AuthContext";

const CATEGORY_IMAGES = {
  Tech: "https://images.unsplash.com/photo-1517336714731-489689fd1ca8?w=1200",
  Home: "https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=1200",
  Fashion: "https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=1200",
  Beauty: "https://images.unsplash.com/photo-1596462502278-27bfdc403348?w=1200",
  Sports: "https://images.unsplash.com/photo-1526506118085-60ce8714f8c5?w=1200",
  Gym: "https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=1200",
};

export default function Home() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [settings, setSettings] = useState({});
  const [recs, setRecs] = useState([]);
  const [recStrategy, setRecStrategy] = useState(null);
  const [catRotation, setCatRotation] = useState(0);
  const { user } = useAuth();

  useEffect(() => {
    api.get("/products?limit=8").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
    api.get("/settings").then((r) => setSettings(r.data || {})).catch(() => {});
  }, []);

  // Rotate which 6 categories are visible on each fresh visit / sign-in so that
  // future categories beyond the first 6 also get screen time.
  useEffect(() => {
    setCatRotation(Math.floor(Math.random() * Math.max(categories.length || 1, 1)));
  }, [user, categories.length]);

  const visibleCategories = (() => {
    if (categories.length <= 6) return categories;
    const start = catRotation % categories.length;
    const window = [];
    for (let i = 0; i < 6; i++) {
      window.push(categories[(start + i) % categories.length]);
    }
    return window;
  })();

  // Personalised "Recommended for you" — refetch when sign-in state changes.
  // The backend rotates between a HOT (most-clicked) ranking and an AI_PICK
  // (personalised score) ranking on each request, surfaced in the
  // X-Rec-Strategy response header so we can show a matching micro-label.
  useEffect(() => {
    api.get("/recommendations/for-me?limit=8")
      .then((r) => {
        setRecs(r.data);
        setRecStrategy(r.headers?.["x-rec-strategy"] || null);
      })
      .catch(() => setRecs([]));
  }, [user]);

  return (
    <div>
      {settings.home_video_url && (
        <YouTubeHero url={settings.home_video_url} title={settings.home_video_title} />
      )}

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-neutral-200">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-10 pb-16 lg:pt-20 lg:pb-28 grid lg:grid-cols-12 gap-8 items-end">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] font-semibold text-[#0033FF] mb-6">
              <Sparkles className="w-3.5 h-3.5" /> Vol. 01 — The Considered Edit
            </div>
            <h1 className="font-display font-black leading-[0.9] tracking-tight text-5xl sm:text-7xl lg:text-[8.5rem]">
              Shop <span className="text-[#0033FF]">less.</span>
              <br />
              Own <em className="not-italic underline decoration-[6px] underline-offset-[10px]">more</em>.
            </h1>
            <p className="mt-8 text-base max-w-xl text-neutral-700">
              MATRIX is an editorial marketplace. Every product is chosen by an independent seller — and displayed without the clutter.
            </p>
            <div className="mt-8 flex gap-3">
              <Link
                to="/products"
                data-testid="hero-shop-btn"
                className="inline-flex items-center gap-2 bg-[#0033FF] hover:bg-[#0022CC] text-white px-6 h-12 font-semibold transition-colors"
              >
                Shop the marketplace <ArrowUpRight className="w-4 h-4" />
              </Link>
              <Link
                to="/seller/apply"
                data-testid="hero-sell-btn"
                className="inline-flex items-center gap-2 border border-black px-6 h-12 font-semibold hover:bg-black hover:text-white transition-colors"
              >
                Sell on MATRIX
              </Link>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="aspect-[4/5] bg-neutral-100 border border-neutral-200 relative overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1633550233861-edf516679c90?w=1200"
                alt="Marketplace"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/70 to-transparent text-white">
                <div className="text-[11px] uppercase tracking-widest opacity-80">Curated weekly</div>
                <div className="font-display font-bold text-2xl">57 new items</div>
              </div>
            </div>
          </div>
        </div>

        {/* Marquee */}
        <div className="border-t border-b border-black bg-[#0033FF] text-white overflow-hidden">
          <div className="marquee whitespace-nowrap py-3 flex gap-12 font-display font-bold text-sm uppercase tracking-[0.2em]">
            {Array.from({ length: 2 }).map((_, j) => (
              <div key={j} className="flex gap-12 shrink-0">
                <span>◆ Free shipping over {fmt(20000)}</span>
                <span>◆ {fmt(2000)} returns</span>
                <span>◆ Independent sellers</span>
                <span>◆ Vetted quality</span>
                <span>◆ Fast dispatch</span>
                <span>◆ Editorial curation</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Affiliate month-to-date earnings (sellers + admins only) */}
      {(user?.role === "seller" || user?.role === "admin") && <AffiliateMonthWidget />}

      {/* Recommended for you — rotates between a "hot right now" ranking
          (what shoppers are clicking) and an AI pick ranking (what the
          system thinks THIS shopper will buy) on every refresh. */}
      {recs.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-16" data-testid="home-recs">
          <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
            <h2 className="font-display font-black text-3xl sm:text-5xl tracking-tight">
              {recStrategy === "hot"
                ? "Hot right now"
                : user ? "Recommended for you" : "Trending now"}
            </h2>
            <div
              data-testid="rec-strategy-label"
              data-strategy={recStrategy || "unknown"}
              className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold"
            >
              {recStrategy === "hot"
                ? "Ranked by shopper clicks"
                : user
                  ? "Picked for you by AI"
                  : "Picked by shopper behavior"}
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6">
            {recs.map((p) => (
              <ProductCard key={p.product_id} product={p} quickAddSource="rec_home" recStrategy={recStrategy} />
            ))}
          </div>
        </section>
      )}

      {/* Categories */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
        <div className="flex items-end justify-between mb-8">
          <h2 className="font-display font-black text-3xl sm:text-5xl tracking-tight">Browse by category</h2>
          <Link to="/categories" className="text-sm font-semibold underline underline-offset-4 hover:text-[#0033FF]">
            View all
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 border-t border-l border-neutral-200">
          {visibleCategories.map((c) => (
            <Link
              key={c.category_id}
              to={`/products?category=${encodeURIComponent(c.name)}`}
              data-testid={`category-${c.slug}`}
              className="group relative aspect-square border-b border-r border-neutral-200 overflow-hidden"
            >
              <img
                src={c.image_url || CATEGORY_IMAGES[c.name] || "https://images.unsplash.com/photo-1607082350899-7e105aa886ae?w=800"}
                alt={c.name}
                className="absolute inset-0 w-full h-full object-cover opacity-90 transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-black/20 group-hover:bg-black/40 transition-colors" />
              <div className="absolute bottom-4 left-4 text-white font-display font-black text-2xl">{c.name}</div>
            </Link>
          ))}
        </div>
      </section>

      {/* Products */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pb-20">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#0033FF] font-bold mb-2">Freshly listed</div>
            <h2 className="font-display font-black text-3xl sm:text-5xl tracking-tight">New on MATRIX</h2>
          </div>
          <Link to="/products?sort=newest" className="text-sm font-semibold underline underline-offset-4 hover:text-[#0033FF]">
            See all →
          </Link>
        </div>
        {products.length === 0 ? (
          <div className="border border-dashed border-neutral-300 p-16 text-center">
            <p className="font-display font-bold text-2xl mb-2">No products yet.</p>
            <p className="text-neutral-600 mb-6">The catalog will populate as sellers join the marketplace.</p>
            <Link
              to="/seller/apply"
              className="inline-flex items-center gap-2 bg-[#0033FF] text-white px-6 h-11 font-semibold"
            >
              Become the first seller
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-10">
            {products.map((p, i) => <ProductCard key={p.product_id} product={p} index={i} />)}
          </div>
        )}
      </section>

      {/* Trust bar */}
      <section className="border-t border-neutral-200 bg-neutral-50">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-14 grid sm:grid-cols-3 gap-10">
          {[
            { icon: Truck, title: "Fast, tracked shipping", desc: `Free over ${fmt(20000)}. Dispatched within 48h.` },
            { icon: ShieldCheck, title: "Vetted sellers", desc: "Every seller approved by our team." },
            { icon: RotateCcw, title: `${fmt(2000)} returns`, desc: "Change your mind? No questions asked." },
          ].map((t) => (
            <div key={t.title} className="flex gap-4">
              <div className="w-11 h-11 bg-black text-white grid place-items-center shrink-0">
                <t.icon className="w-5 h-5" />
              </div>
              <div>
                <div className="font-display font-bold text-lg">{t.title}</div>
                <div className="text-sm text-neutral-600 mt-1">{t.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
